// MP431 — Cookie notice.
//
// Deliberately a one-line NOTICE, not a consent dialog. The web app
// sets no cookies at all: Firebase Auth keeps the sign-in session in
// IndexedDB, there is no analytics or advertising SDK (grep
// `document.cookie`, `gtag`, `firebase/analytics` in apps/web — none),
// and the only other browser storage is per-viewer UI preferences.
// Under PECR / ePrivacy strictly-necessary storage needs no consent,
// so asking for it would be theatre. The notice tells the truth and
// links to the privacy policy; dismissal is remembered in
// localStorage (itself strictly-necessary — it stores one flag).
//
// If an analytics or marketing tool is ever added, this component is
// the wrong shape: replace it with a real consent gate that blocks
// the tool until accepted.

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

const DISMISS_KEY = 'xone.cookieNotice.dismissed.v1';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function CookieNotice() {
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(readDismissed());
  }, []);

  if (dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Storage unavailable (private mode) — hide for this view only.
    }
    setDismissed(true);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-center gap-3 px-4 py-2 text-xs text-slate-800 shadow-2xl dark:text-foreground"
      // Memory rule feedback_floating_layer_bg: floating layers need a
      // hardcoded grey background or they blend into the page.
      style={{ backgroundColor: 'hsl(var(--popover))' }}
    >
      <span>
        X-One sets no cookies and uses no analytics or advertising trackers. Your sign-in
        session is kept in your browser's own storage, which is strictly necessary.{' '}
        <a href="/privacy" className="underline">
          Privacy policy
        </a>
      </span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss notice"
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-slate-300"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
